import { React, useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';

import { useTheme } from '../../Context/ThemeContext';
import { useLanguage } from '../../Context/LanguageContext';
import { lightTheme, darkTheme } from '../../Styles/theme';
import FetchModule from '../../Model/Share/Network/FetchModule';

import { ReactComponent as LeftArrowSvg } from '../../image/LeftArrow.svg';

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;
  color: ${props => props.color};
  cursor: ${props => props.cursor || 'default'};
  margin: 0;
  padding: 0;
`;

const CountryCodeSelectBox = forwardRef(function CountryCodeSelectBox({ selectedCountry, setSelectedCountry, disabled }, ref) {
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { languageMode } = useLanguage();

  const [countryList, setCountryList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useImperativeHandle(ref, () => ({
    setByCode: (code) => {
      const found = countryList.find(c => c.countryCode === code);
      if (found) setSelectedCountry(found);
    }
  }));

  useEffect(() => {
    const fetchModule = new FetchModule();
    (async () => {
      const responseData = await fetchModule.getDataOfCoachVersion2('sms_cert/country', 'GET');
      if (responseData.status === 200) {
        setCountryList(responseData.data);
        const korea = responseData.data.find(c => c.countryCode === '+82');
        if (korea) setSelectedCountry(korea);
      }
    })();
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <CountryCodeSelector ref={dropdownRef}>
      <CountryCodeButton $disabled={disabled} onClick={() => { if (!disabled) setIsOpen(prev => !prev); }}>
        <CountryFlag src={selectedCountry.flagUrl} alt='' />
        <CountryCodeArrow $isOpen={isOpen}>
          <LeftArrowSvg width={12} height={12} />
        </CountryCodeArrow>
      </CountryCodeButton>
      {isOpen && !disabled && (
        <CountryCodeDropdown>
          {countryList.map((country, idx) => (
            <CountryCodeItem key={idx} onClick={() => { setSelectedCountry(country); setIsOpen(false); }}>
              <CountryFlag src={country.flagUrl} alt='' />
              <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={country.countryCode === selectedCountry.countryCode ? theme.primary : theme.text} cursor='pointer'>
                {languageMode === 'ko' ? country.countryNameKR : country.countryNameEN}
              </Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={country.countryCode === selectedCountry.countryCode ? theme.primary : theme.subText} cursor='pointer' style={{ marginLeft: 'auto' }}>{country.countryCode}</Text>
            </CountryCodeItem>
          ))}
        </CountryCodeDropdown>
      )}
    </CountryCodeSelector>
  );
});

export default CountryCodeSelectBox;

const CountryCodeSelector = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const CountryCodeButton = styled.div`
  box-sizing: border-box;

  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 5px;
  gap: 5px;

  width: 70px;
  height: 32px;

  border: 1px solid ${props => props.theme.line};
  border-radius: 5px;

  cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.4 : 1};
  transition: opacity 0.3s ease-in-out;
`;

const CountryFlag = styled.img`
  width: 32px;
  height: 22px;

  border-radius: 5px;
  object-fit: cover;
`;

const CountryCodeArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 23px;
  height: 24px;

  transform: ${props => props.$isOpen ? 'rotate(90deg)' : 'rotate(-90deg)'};
  transition: transform 0.2s ease-in-out;

  & path {
    fill: ${props => props.theme.line};
  }
`;

const CountryCodeDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;

  display: flex;
  flex-direction: column;

  width: 220px;
  max-height: 200px;
  overflow-y: auto;

  background: ${props => props.theme.subBackground};
  border: 1px solid ${props => props.theme.line};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};

  z-index: 100;

  &::-webkit-scrollbar {
    width: 4px;
    background: ${props => props.theme.subBackground};
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.line};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.scrollBar};
    border-radius: 3px;
  }
`;

const CountryCodeItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 10px;
  gap: 8px;

  cursor: pointer;

  &:hover {
    background: ${props => props.theme.buttonFill};
  }
`;
